import React, { createContext, useState, useContext } from 'react';

const DataContext = createContext();

export const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
    "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
    "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
    "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
    "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
    "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
    "Wisconsin", "Wyoming"
];

export const useData = () => {
    return useContext(DataContext);
};

export const DataProvider = ({ children }) => {
    const [data, setData] = useState(null);
    const [franklinData, setFranklinData] = useState(null);
    const [unifiedDevices, setUnifiedDevices] = useState(null);
    const [utilities, setUtilities] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchAllData = async (accessToken) => {
        if (!accessToken) return;

        // If data is already populated, we don't fetch again unless forced (which we can handle separately if needed)
        // Checks are individual so if one failed or is missing, we try it.
        if (data && franklinData && unifiedDevices && utilities) {
            return;
        }

        setLoading(true);
        setError(null);

        const connectListApi = process.env.REACT_APP_BASE_URL + "get/texture/device/";
        const franklinApi = process.env.REACT_APP_BASE_URL + "get/fwh/device/";
        const unifiedListApi = process.env.REACT_APP_BASE_URL + "devices/unified-list/";
        const utilitiesApi = process.env.REACT_APP_BASE_URL + "utility-rates/";

        try {
            // Parallel fetch could be faster
            const promises = [];

            if (!data) {
                promises.push(
                    fetch(connectListApi, {
                        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
                    }).then(async res => {
                        if (res.ok) {
                            const result = await res.json();
                            const allowedTypes = ["inverter", "thermostat", "vehicle", "battery", "franklinwh"];
                            const filtered = result.filter(device => {
                                if (!device.device_type || typeof device.device_type !== "string") return false;
                                const normalized = device.device_type.toLowerCase().replace(/\s+/g, "");
                                return allowedTypes.includes(normalized);
                            });
                            setData(filtered);
                        }
                    })
                );
            }

            if (!franklinData) {
                promises.push(
                    fetch(franklinApi, {
                        method: "GET",
                        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
                    }).then(async res => {
                        if (res.ok) {
                            const result = await res.json();
                            setFranklinData(result);
                        }
                    })
                );
            }

            if (!unifiedDevices) {
                promises.push(
                    fetch(unifiedListApi, {
                        method: "GET",
                        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
                    }).then(async res => {
                        if (res.ok) {
                            const result = await res.json();
                            setUnifiedDevices(result.results || []);
                        }
                    })
                );
            }

            if (!utilities) {
                promises.push(
                    fetch(utilitiesApi, {
                        headers: { 'Authorization': `Bearer ${accessToken}` }
                    }).then(async res => {
                        if (res.ok) {
                            const result = await res.json();
                            // Handle both array and object with results property
                            const utilitiesData = Array.isArray(result) ? result : (result.results || result.data || []);
                            setUtilities(utilitiesData);
                            console.log('Utilities loaded:', utilitiesData);
                        }
                    })
                );
            }

            await Promise.all(promises);

        } catch (err) {
            console.error("Error fetching data in context:", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const clearData = () => {
        setData(null);
        setFranklinData(null);
        setUnifiedDevices(null);
        setUtilities(null);
    };

    return (
        <DataContext.Provider value={{
            data, setData,
            franklinData, setFranklinData,
            unifiedDevices, setUnifiedDevices,
            utilities, setUtilities,
            loading,
            error,
            fetchAllData,
            clearData
        }}>
            {children}
        </DataContext.Provider>
    );
};
